import { DataService } from '../../dataService';
import { AxiosPromise } from 'axios';
import {
  Currency,
  Division,
  DivisionSteward,
  DocumentSecurityLevel,
  Employee,
  SaleType,
  Stewardship,
  TransactionType
} from '../models/AIMModels';
import { transactionTeamController } from './TransactionTeamController';
import { DivisionStewardshipParams, StateProvinceByStateParams } from '../models/APIModels';
import { opportunityController } from './OpportunityController';
import { endpoints } from './endpoints/endpoints';
import { attachmentsController } from './AttachmentsController';
import { ENV } from '../envUtils';
import { Country, StateProvince } from '../models/PrismTransactionModels';

/**
 * Factory function that constructs the AIM API controller object with HTTP methods for fetching divisions, employees, transaction types, currencies, stewardships, and other AIM resources.
 *
 * Takes a DataService instance and returns an object of Axios-based methods for each AIM endpoint; these are thin wrappers that pass routes and optionally form data.
 */
export const aimController = (api: DataService) => ({
  getDivisions: (): AxiosPromise<Division[]> => api.get({ route: [endpoints.divisions] }),
  getEmployees: (): AxiosPromise<Employee[]> => api.get({ route: [endpoints.employees] }),
  getTransactionTypes: (): AxiosPromise<TransactionType[]> => api.get({ route: [endpoints.employees] }),
  getDivisionEmployees: ({ aimDivision }: Pick<Division, 'aimDivision'>): AxiosPromise<Employee[]> =>
    api.get({ route: [`${ENV}/division/${aimDivision}/employees`] }),
  getEcho: (): AxiosPromise => api.get({ route: [`${ENV}/echo`] }),
  getCurrencies: (): AxiosPromise<Currency[]> => api.get({ route: [`${ENV}/currencies`] }),
  getCurrency: ({ currency }: Pick<Currency, 'currency'>): AxiosPromise<Currency> =>
    api.get({ route: [`${ENV}/currency/${currency}`] }),
  getDivisionStewardship: ({
    aimDivision,
    aimStewardship
  }: DivisionStewardshipParams): AxiosPromise<Stewardship> =>
    api.get({ route: [`${ENV}/division/${aimDivision}/steward/${aimStewardship}`] }),
  getDivisionStewards: ({
    aimDivision
  }: Pick<Division, 'aimDivision'>): AxiosPromise<Array<DivisionSteward>> =>
    api.get({ route: [endpoints.divisionStewards(aimDivision)] }),
  getStewardships: (): AxiosPromise<Stewardship[]> => api.get({ route: [`${ENV}/stewardships`] }),
  getStewardshipsDivision: ({ aimDivision }: Pick<Division, 'aimDivision'>): AxiosPromise<Division> =>
    api.get({ route: [`${ENV}/stewardships/division/${aimDivision}`] }),
  getStewardShip: ({ aimStewardship }: Pick<Stewardship, 'aimStewardship'>): AxiosPromise =>
    api.get({ route: [`${ENV}/stewardship/${aimStewardship}`] }),
  getStewardShipByStewardDivision: ({
    aimStewardship,
    aimDivision
  }: DivisionStewardshipParams): AxiosPromise<Stewardship> =>
    api.get({ route: [`${ENV}/stewardship/${aimStewardship}/division/${aimDivision}`] }),
  getDocumentSecurityLevels: (): AxiosPromise<DocumentSecurityLevel[]> =>
    api.get({ route: [endpoints.documentSecurityLevels] }),
  getDocumentSecurityLevel: ({
    aimDocumentSecurityLevel
  }: Pick<DocumentSecurityLevel, 'aimDocumentSecurityLevel'>): AxiosPromise<DocumentSecurityLevel> =>
    api.get({ route: [`${ENV}/documentsecuritylevel/${aimDocumentSecurityLevel}`] }),
  getEmployeesByDivision: ({ aimDivision }: Pick<Division, 'aimDivision'>): AxiosPromise<Employee[]> =>
    api.get({ route: [`${ENV}/employees/division/${aimDivision}`] }),
  getEmployee: ({ aimEmployee }: any): AxiosPromise<Employee> =>
    api.get({ route: [`${ENV}/employee/${aimEmployee}`] }),
  getSaleTypes: (): AxiosPromise<SaleType[]> => api.get({ route: [`${ENV}/saletypes`] }),
  getSaleType: ({ aimSaleType }: Pick<SaleType, 'aimSaleType'>): AxiosPromise<SaleType> =>
    api.get({ route: [`${ENV}/saletype/${aimSaleType}`] }),
  getCountries: (): AxiosPromise<Array<Country>> => api.get({ route: [`${ENV}/countries`] }),

  transactionTeam: { ...transactionTeamController(api) },
  ...transactionTeamController(api),
  opportunity: { ...opportunityController(api) },
  ...opportunityController(api),
  attachments: { ...attachmentsController(api) },
  ...attachmentsController(api)
});
